/**
 * Static lists of allowed icon names for resources.
 *
 * These arrays are intentionally hardcoded as plain strings so that files
 * needing only icon-name validation (e.g. zod schemas used by temporal workers)
 * can import them without pulling in `@dust-tt/sparkle`.
 *
 * When a new icon is added to `ActionIcons` or `InternalActionIcons` in sparkle
 * / resources_icons.tsx, it must also be added here. The type assertions in
 * `resources_icons.tsx` will cause a compile error if the lists drift.
 */

export const CUSTOM_RESOURCE_ALLOWED = [
  "ActionArmchairIcon",
  "ActionArrowDownOnSquareIcon",
  "ActionArrowUpOnSquareIcon",
  "ActionAtomIcon",
  "ActionAttachmentIcon",
  "ActionBankIcon",
  "ActionBarcodeIcon",
  "ActionBeerIcon",
  "ActionBookOpenIcon",
  "ActionBracesIcon",
  "ActionBrainIcon",
  "ActionBriefcaseIcon",
  "ActionBuildingIcon",
  "ActionCalculatorIcon",
  "ActionCalendarCheckIcon",
  "ActionCalendarIcon",
  "ActionCameraIcon",
  "ActionCardIcon",
  "ActionCarIcon",
  "ActionChatBubbleBottomCenterTextIcon",
  "ActionChatBubbleThoughtIcon",
  "ActionCheckCircleIcon",
  "ActionClipboardIcon",
  "ActionCloudArrowDownIcon",
  "ActionCloudArrowLeftRightIcon",
  "ActionCloudArrowUpIcon",
  "ActionCodeBlockIcon",
  "ActionCodeBoxIcon",
  "ActionCommand1Icon",
  "ActionCommandIcon",
  "ActionCommunityIcon",
  "ActionCompanyIcon",
  "ActionCubeIcon",
  "ActionCupIcon",
  "ActionCustomerServiceIcon",
  "ActionDashboardIcon",
  "ActionDatabaseIcon",
  "ActionDocumentIcon",
  "ActionDocumentPileIcon",
  "ActionDocumentPlusIcon",
  "ActionDocumentTextIcon",
  "ActionDoubleQuotesIcon",
  "ActionEmotionLaughIcon",
  "ActionExternalLinkIcon",
  "ActionEyeIcon",
  "ActionEyeSlashIcon",
  "ActionFilmIcon",
  "ActionFilterIcon",
  "ActionFireIcon",
  "ActionFlagIcon",
  "ActionFlightLandIcon",
  "ActionFlightTakeoffIcon",
  "ActionFolderAddIcon",
  "ActionFolderIcon",
  "ActionFolderOpenIcon",
  "ActionFrameIcon",
  "ActionFullscreenExitIcon",
  "ActionFullscreenIcon",
  "ActionGamepadIcon",
  "ActionGitBranchIcon",
  "ActionGitForkIcon",
  "ActionGlobeAltIcon",
  "ActionGlobeIcon",
  "ActionGraduationCapIcon",
  "ActionHandHeartIcon",
  "ActionHandThumbDownIcon",
  "ActionHandThumbUpIcon",
  "ActionHeartIcon",
  "ActionHomeIcon",
  "ActionHospitalIcon",
  "ActionIdentityIcon",
  "ActionImageIcon",
  "ActionInboxIcon",
  "ActionIncludeIcon",
  "ActionLayoutIcon",
  "ActionLightbulbIcon",
  "ActionListCheckIcon",
  "ActionListIcon",
  "ActionLockIcon",
  "ActionLogoutIcon",
  "ActionMagicIcon",
  "ActionMagnifyingGlassIcon",
  "ActionMailAiIcon",
  "ActionMailCloseIcon",
  "ActionMailIcon",
  "ActionMapIcon",
  "ActionMapPinIcon",
  "ActionMarkPenIcon",
  "ActionMedalIcon",
  "ActionMegaphoneIcon",
  "ActionMenuIcon",
  "ActionMicIcon",
  "ActionMoonIcon",
  "ActionMovieIcon",
  "ActionNoiseIcon",
  "ActionNumbersIcon",
  "ActionPaintIcon",
  "ActionPencilSquareIcon",
  "ActionPieChartIcon",
  "ActionPinDistanceIcon",
  "ActionPingPongIcon",
  "ActionPlanetIcon",
  "ActionPlusCircleIcon",
  "ActionPlusIcon",
  "ActionPrinterIcon",
  "ActionPushpinIcon",
  "ActionRainbowIcon",
  "ActionRobotIcon",
  "ActionRocketIcon",
  "ActionSafeIcon",
  "ActionSaveIcon",
  "ActionScalesIcon",
  "ActionScanIcon",
  "ActionSeedlingIcon",
  "ActionServerIcon",
  "ActionShakeHandsIcon",
  "ActionShipIcon",
  "ActionShirtIcon",
  "ActionShoppingBasketIcon",
  "ActionSlideshowIcon",
  "ActionSparklesIcon",
  "ActionSpeakIcon",
  "ActionSquare3Stack3DIcon",
  "ActionStopSignIcon",
  "ActionStoreIcon",
  "ActionSunIcon",
  "ActionSwordIcon",
  "ActionTableIcon",
  "ActionTagIcon",
  "ActionTestTubeIcon",
  "ActionTimeIcon",
  "ActionTrainIcon",
  "ActionTranslateIcon",
  "ActionTrashIcon",
  "ActionTrophyIcon",
  "ActionTShirtIcon",
  "ActionUmbrellaIcon",
  "ActionUserGroupIcon",
  "ActionUserIcon",
  "ActionVidiconIcon",
  "ActionVolumeUpIcon",
  "ActionXCircleIcon",
] as const;

export type CustomResourceIconType = (typeof CUSTOM_RESOURCE_ALLOWED)[number];

export const isCustomResourceIconType = (
  icon: string
): icon is CustomResourceIconType =>
  (CUSTOM_RESOURCE_ALLOWED as readonly string[]).includes(icon);

export const INTERNAL_ALLOWED_ICONS = [
  "ActionAtomIcon",
  "ActionBrainIcon",
  "ActionChatBubbleBottomCenterTextIcon",
  "ActionChatBubbleThoughtIcon",
  "ActionCheckCircleIcon",
  "ActionCloudArrowLeftRightIcon",
  "ActionDocumentTextIcon",
  "ActionEmotionLaughIcon",
  "ActionFrameIcon",
  "ActionGitBranchIcon",
  "ActionGlobeAltIcon",
  "ActionImageIcon",
  "ActionLightbulbIcon",
  "ActionListCheckIcon",
  "ActionLockIcon",
  "ActionMagnifyingGlassIcon",
  "ActionMegaphoneIcon",
  "ActionNoiseIcon",
  "ActionPieChartIcon",
  "ActionRobotIcon",
  "ActionScanIcon",
  "ActionSlideshowIcon",
  "ActionSpeakIcon",
  "ActionStoreIcon",
  "ActionTableIcon",
  "ActionTimeIcon",
  "AdomikLogo",
  "AmplitudeLogo",
  "ApifyLogo",
  "AsanaLogo",
  "AttioLogo",
  "AshbyLogo",
  "BigQueryLogo",
  "ToolsIcon",
  "CanvaLogo",
  "ClariLogo",
  "CommandLineIcon",
  "ConfluenceLogo",
  "ContentsquareLogo",
  "CostoryLogo",
  "DriveLogo",
  "FathomLogo",
  "FreshserviceLogo",
  "FrontLogo",
  "GammaLogo",
  "GcalLogo",
  "GithubLogo",
  "GitlabLogo",
  "GmailLogo",
  "GongLogo",
  "GoogleSpreadsheetLogo",
  "GranolaLogo",
  "GuruLogo",
  "HexLogo",
  "HubspotLogo",
  "IntercomLogo",
  "JiraLogo",
  "LemlistLogo",
  "LinearLogo",
  "LumaLogo",
  "MicrosoftExcelLogo",
  "MicrosoftLogo",
  "MicrosoftOutlookLogo",
  "MicrosoftTeamsLogo",
  "MiroLogo",
  "ModjoLogo",
  "MondayLogo",
  "NaptaLogo",
  "NetSuiteLogo",
  "NotionLogo",
  "OpenaiLogo",
  "PowerBiLogo",
  "PraizLogo",
  "ProductboardLogo",
  "PuzzleIcon",
  "SalesforceLogo",
  "SemrushLogo",
  "SalesloftLogo",
  "ShopifyLogo",
  "SlabLogo",
  "SlackLogo",
  "SnowflakeLogo",
  "StatuspageLogo",
  "StripeLogo",
  "SupabaseLogo",
  "TerminalSquareIcon",
  "UkgLogo",
  "ValTownLogo",
  "VantaLogo",
  "YoutrustLogo",
  "ZendeskLogo",
] as const;

export type InternalAllowedIconType = (typeof INTERNAL_ALLOWED_ICONS)[number];

export const isInternalAllowedIcon = (
  icon: string
): icon is InternalAllowedIconType =>
  (INTERNAL_ALLOWED_ICONS as readonly string[]).includes(icon);
